import React, { useCallback, useState } from "react";

import { useWatch } from "antd/es/form/Form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { isThisYear, startOfDay } from "date-fns";
import { defineMessages, useIntl } from "react-intl";

import { Button, FormItem } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import FormValues from "@app/component/createEditEntity/Activity/types/FormValues";
import { ClearableDatePicker } from "@app/component/input/DatePicker";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { formatDate } from "@app/util/formatters";
import isValidDate from "@app/util/isValidDate";

import styles from "./ActivityEndDate.module.scss";

interface Props {
  disabledTooltip?: string;
  form: FormInstance<FormValues>;
  onChange?: () => void;
}

const messages = defineMessages({
  changeDate: {
    id: "createActivityModal.footer.scheduleActivity.changeEndDate",
    defaultMessage: "Change End Date",
    description: "Change End Date",
  },
  removeDateTime: {
    id: "createActivityModal.footer.scheduleActivity.endDate.remove",
    defaultMessage: "Remove",
    description: "Remove",
  },
  selectDate: {
    id: "createActivityModal.footer.scheduleActivity.selectEndDate",
    defaultMessage: "Select date",
    description: "Select date",
  },
});

const ActivityEndDate = ({ disabledTooltip, form, onChange }: Props) => {
  const intl = useIntl();

  const [open, setOpen] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const startDate = useWatch(ActivityFieldName.START_AT, form);
  const startDateValue = startDate ? startOfDay(startDate) : undefined;
  const dateValue = useWatch(ActivityFieldName.END_AT, form);
  const currentDate = isValidDate(dateValue) ? dateValue : undefined;
  const isCurrentYear = currentDate && isThisYear(currentDate);
  const dateFormat = intl.formatMessage(
    {
      id: "createEditEntity.activity.endDate.format",
      defaultMessage: "{currentYear, select, true {eeee, MMM d} other {eeee, MMM d, yyyy}}",
      description: "Date format for current date",
    },
    {
      currentYear: isCurrentYear,
    }
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setTooltipVisible(false);
      }
      setOpen(open);
    },
    [setOpen, setTooltipVisible]
  );

  const handleClearDateClick = useCallback(() => {
    form.setFieldValue(ActivityFieldName.END_AT, undefined);

    setTooltipVisible(false);
    setOpen(false);

    onChange?.();
  }, [form, onChange, setOpen, setTooltipVisible]);

  const handleDisabledDate = useCallback(
    (date: Date) => {
      return !startDateValue || startOfDay(date) < startDateValue;
    },
    [startDateValue]
  );

  const handleClick = useCallback(() => {
    setTooltipVisible(false);
    setOpen(true);
  }, [setTooltipVisible, setOpen]);

  return (
    <Tooltip
      destroyTooltipOnHide
      mouseLeaveDelay={0}
      onOpenChange={setTooltipVisible}
      open={tooltipVisible}
      overlayClassName={styles.tooltip}
      placement="top"
      title={disabledTooltip ?? intl.formatMessage(messages.changeDate)}
      trigger={open ? [] : ["hover"]}
    >
      <div className={styles.container}>
        <FormItem name={ActivityFieldName.END_AT} noStyle>
          <ClearableDatePicker
            allowClear={false}
            bordered={false}
            disabled={!!disabledTooltip}
            disabledDate={handleDisabledDate}
            dropdownClassName={styles.panel}
            inputReadOnly
            onOpenChange={handleOpenChange}
            open={open}
            renderExtraFooter={() => (
              <ButtonLink className={styles.clear} onClick={handleClearDateClick}>
                {intl.formatMessage(messages.removeDateTime)}
              </ButtonLink>
            )}
            showTime={false}
            suffixIcon={<></>}
          />
        </FormItem>

        <Button
          className={cn(styles.btn, { [styles.active]: open, [styles.hasValue]: !!currentDate })}
          disabled={!!disabledTooltip}
          onClick={handleClick}
          type="control"
        >
          {currentDate
            ? formatDate(currentDate, dateFormat)
            : intl.formatMessage(messages.selectDate)}
        </Button>
      </div>
    </Tooltip>
  );
};

export default ActivityEndDate;
